import React, { useCallback, useEffect, useState } from 'react';

/**
 * Full-screen image viewer with prev/next navigation (detail pages and galleries).
 */
const ImageLightboxModal = ({
  isOpen,
  images = [],
  startIndex = 0,
  onClose,
}) => {
  const [index, setIndex] = useState(startIndex);
  const slides = Array.isArray(images) ? images.filter((s) => s?.url) : [];
  const count = slides.length;
  const current = count > 0 ? slides[Math.min(index, count - 1)] : null;

  useEffect(() => {
    if (isOpen) {
      setIndex(Math.max(0, Math.min(startIndex, Math.max(0, count - 1))));
    }
  }, [isOpen, startIndex, count]);

  const goNext = useCallback(() => {
    if (count <= 1) return;
    setIndex((prev) => (prev + 1) % count);
  }, [count]);

  const goPrev = useCallback(() => {
    if (count <= 1) return;
    setIndex((prev) => (prev - 1 + count) % count);
  }, [count]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, goNext, goPrev]);

  if (!isOpen || !current) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-20 bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 transition-colors"
        aria-label="Close image viewer"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-colors"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-colors"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      <div
        className="flex flex-col items-center max-w-7xl max-h-full w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.url}
          alt={current.alt || 'Image'}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
        />
        {count > 1 && (
          <p className="mt-3 text-sm text-white/90 bg-black/40 px-3 py-1 rounded-full">
            {index + 1} of {count}
          </p>
        )}
        {count > 1 && (
          <div className="mt-4 flex gap-2 max-w-full overflow-x-auto px-2 pb-1">
            {slides.map((slide, thumbIndex) => (
              <button
                key={slide.id ?? thumbIndex}
                type="button"
                onClick={() => setIndex(thumbIndex)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                  thumbIndex === index ? 'border-white' : 'border-transparent hover:border-gray-400'
                }`}
                aria-label={`View image ${thumbIndex + 1}`}
              >
                <img
                  src={slide.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageLightboxModal;
